import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useTranslation } from 'next-i18next';
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';

import Availability from '@/containers/MarketplacePage/FilterSideBar/Availability';
import MasterSearchBar from '@/containers/MarketplacePage/FilterSideBar/MasterSearchBar';
import PriceRange from '@/containers/MarketplacePage/FilterSideBar/PriceRange';
import SortSelect from '@/containers/MarketplacePage/FilterSideBar/SortSelect';
import TagSearchBar from '@/containers/MarketplacePage/FilterSideBar/TagSearchBar';
import ChevronRight2 from '@/icons/chevron-right2.svg';

import styles from './FilterSideBar.module.scss';
import Verification from './Verification';
import MarketplaceItemsContext from '@/contexts/MarketplaceItemsContext';

type Props = {
  onCloseButtonClick(): void;
  isShowingNFTs: boolean;
  setIsShowingNFTs(value: boolean): void;
};

const FilterSideBar = forwardRef<HTMLDivElement, Props>(
  ({ onCloseButtonClick, isShowingNFTs, setIsShowingNFTs }, ref) => {
    const { t } = useTranslation();
    const sidebarContentRef = useRef<HTMLDivElement>(null);
    const [sortSelectIsCollapsed, setSortSelectIsCollapsed] = useState(false);

    useEffect(() => {
      if (sidebarContentRef.current) {
        disableBodyScroll(sidebarContentRef.current);
      }
    }, []);

    useEffect(() => {
      return () => {
        clearAllBodyScrollLocks();
      };
    }, []);

    const showNFTs = () => {
      setIsShowingNFTs(true);
    };

    const showProfiles = () => {
      setIsShowingNFTs(false);
    };

    const initialCheckboxState = new Map<string, boolean>([
      ['artist', true],
      ['collector', true],
    ]);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchTag, setSearchTag] = useState('');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    const [checkboxState, setCheckboxState] = useState(initialCheckboxState);

    const handleResetFilter = () => {
      scrollWindowToTop();
      setSearchTerm('');
      setSearchTag('');
      setPriceMin('');
      setPriceMax('');
      if (isShowingNFTs) {
        setSearchConditionNFTs(initialSearchConditionNFTs);
      } else {
        setSearchConditionUsers(initialSearchConditionUsers);
        setCheckboxState(initialCheckboxState);
      }
      setShowRandom(false);
      loadCreations(true);
    };

    const handleRandomButton = () => {
      // When the random button is clicked we want to reset the filters, so we can call the randomize function with a new set of IDs
      scrollWindowToTop();
      setSearchTerm('');
      setSearchTag('');
      setPriceMin('');
      setPriceMax('');
      if (isShowingNFTs) {
        setSearchConditionNFTs(initialSearchConditionNFTs);
      } else {
        setSearchConditionUsers(initialSearchConditionUsers);
        setCheckboxState(initialCheckboxState);
      }
      if (showRandom) {
        setShowRandom(false);
      }
      setShowRandom(true);
    };

    const scrollWindowToTop = () => {
      window.scroll({
        top: 0,
      });
    };

    const {
      initialSearchConditionNFTs,
      initialSearchConditionUsers,
      setSearchConditionNFTs,
      setSearchConditionUsers,
      loadCreations,
      showRandom,
      setShowRandom,
    } = useContext(MarketplaceItemsContext);

    return (
      <div className={styles.filterSidebar} ref={ref}>
        <div className={styles.filterSidebarContent}>
          <div className={styles.switchingModal}>
            <button
              className={
                isShowingNFTs ? styles.NFTButtonActive : styles.NFTButton
              }
              onClick={showNFTs}
            >
              {t('MarketplaceProfiles.NFTS')}
            </button>
            <button
              className={
                !isShowingNFTs
                  ? styles.ProfilesButtonActive
                  : styles.ProfilesButton
              }
              onClick={showProfiles}
            >
              {t('MarketplaceProfiles.Profiles')}
            </button>
          </div>
          <div className={styles.filterButtonContainer}>
            {/* {isShowingNFTs && (
              <button
                className={styles.filterButton}
                onClick={() => handleRandomButton()}
              >
                {t('filterSidebar.filterButton.randomize')}
              </button>
            )} */}
            <button
              className={styles.filterButton}
              onClick={() => {
                handleResetFilter();
              }}
            >
              {t('filterSidebar.filterButton.reset')}
            </button>
          </div>
          <MasterSearchBar
            isShowingNFTs={isShowingNFTs}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
          <div className={styles.title}>
            {t('filterSidebar.heading')}
            <button
              type="button"
              onClick={onCloseButtonClick}
              className={styles.sidebarCloseButton}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 18H16V16H3V18ZM3 13H13V11H3V13ZM3 6V8H16V6H3ZM21 15.59L17.42 12L21 8.41L19.59 7L14.59 12L19.59 17L21 15.59Z"
                  fill="#323232"
                />
              </svg>
            </button>
          </div>
          <TagSearchBar
            isShowingNFTs={isShowingNFTs}
            searchTerm={searchTag}
            setSearchTerm={setSearchTag}
          />
          {!isShowingNFTs && (
            <Verification
              checkboxState={checkboxState}
              setCheckboxState={setCheckboxState}
            />
          )}
          {isShowingNFTs && <Availability />}
          {/* Sort */}
          <div
            className={[styles.conditionGroup, styles.sidebarSubsection].join(
              ' '
            )}
          >
            <div className={styles.title}>
              {t('filterSidebar.sort.heading')}
            </div>
            <ChevronRight2
              className={
                sortSelectIsCollapsed
                  ? styles.collapseButtonClicked
                  : styles.collapseButton
              }
              onClick={() => {
                setSortSelectIsCollapsed(!sortSelectIsCollapsed);
              }}
            />
            {!sortSelectIsCollapsed && (
              <SortSelect isShowingNFTs={isShowingNFTs} />
            )}
          </div>
          {isShowingNFTs && (
            <PriceRange
              priceMin={priceMin}
              setPriceMin={setPriceMin}
              priceMax={priceMax}
              setPriceMax={setPriceMax}
            />
          )}
        </div>
      </div>
    );
  }
);

export default FilterSideBar;
